import React, { createContext, forwardRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Dropdown } from "react-bootstrap";

import usePortalDropdownMenuPosition from "./usePortalDropdownMenuPosition";
import claimOpenDropdown from "./openDropdownRegistry";

export const PortalDropdownContext = createContext(null);

const DEFAULT_TOGGLE_ICON = "fa-solid fa-ellipsis";

/** Focusable menu entries, for arrow-key navigation. Matches DropdownMenuItem's markup. */
const MENU_ITEM_SELECTOR = ".dropdown-item:not(:disabled)";

/**
 * Items a keyboard user can actually reach. Menus carry responsive entries — the
 * conversation menu's mobile-only actions, for instance — that are display:none at
 * the current width, and focusing one of those silently does nothing.
 */
function focusableItems(menu) {
  return [...menu.querySelectorAll(MENU_ITEM_SELECTOR)].filter(
    (item) => globalThis.getComputedStyle(item).display !== "none",
  );
}

/**
 * Custom toggle that prevents Bootstrap's caret and supports custom styling.
 */
const PortalDropdownToggle = forwardRef(
  (
    {
      id,
      onClick,
      className,
      toggleClassName,
      toggleStyle,
      disabled,
      toggleAriaLabel,
      toggleLabel = null,
      toggleIcon = DEFAULT_TOGGLE_ICON,
    },
    ref,
  ) => {
    // Filter out dropdown-toggle class to prevent Bootstrap's caret
    const filteredClasses = (className || "").split(" ").filter((c) => c !== "dropdown-toggle");

    return (
      <button
        type="button"
        id={id}
        ref={ref}
        aria-label={toggleAriaLabel}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className={classnames(toggleClassName, filteredClasses)}
        style={{ boxShadow: "none", ...toggleStyle }}
        disabled={disabled}
      >
        {toggleLabel && <span className="me-1">{toggleLabel}</span>}
        <i className={toggleIcon} />
      </button>
    );
  },
);

PortalDropdownToggle.displayName = "PortalDropdownToggle";

/**
 * Custom menu that renders via portal to escape overflow:hidden containers.
 */
const PortalDropdownMenu = forwardRef(
  ({ children, className, "aria-labelledby": labelledBy, menuWidth, menuAlign, show }, ref) => {
    const { attachMenuRef, menuStyle } = usePortalDropdownMenuPosition({
      labelledBy,
      show,
      children,
      forwardedRef: ref,
      menuWidth,
      menuAlign,
    });
    const closeMenu = useContext(PortalDropdownContext);
    const menuNodeRef = useRef(null);
    const setMenuNode = useCallback(
      (node) => {
        menuNodeRef.current = node;
        attachMenuRef(node);
      },
      [attachMenuRef],
    );

    // The menu owns Escape/Tab/arrows itself: it stops key events from leaving
    // (card-level handlers must not see them), which also hides them from
    // react-bootstrap's own document-level handling.
    //
    // This listens on `window` in the CAPTURE phase — the earliest point in the
    // event's path — rather than as a React onKeyDown. Two reasons. React 18
    // delivers portal events from the portal container, well down the path, and
    // something else in the page reacts to the arrow keys before that: pressing
    // ↓ inside an open menu opens the navbar's own dropdown instead of moving
    // between our items. Capturing at the window means nothing downstream, in
    // any phase or delegation scheme, can act on a key aimed at this menu.
    useEffect(() => {
      if (!show) return undefined;

      const handleKeyDown = (event) => {
        const menu = menuNodeRef.current;
        const toggle = document.getElementById(labelledBy);
        const fromMenu = Boolean(menu?.contains(event.target));
        // The first arrow press usually comes while focus is still on the toggle,
        // so that counts as aimed at this menu too.
        const fromToggle = Boolean(toggle?.contains(event.target));
        if (!menu || (!fromMenu && !fromToggle)) return;

        const items = focusableItems(menu);

        switch (event.key) {
          case "Tab":
            // Tab off the toggle should move focus onward as usual; only claim it
            // from inside the menu.
            if (!fromMenu) return;
            if (closeMenu) closeMenu();
            toggle?.focus();
            break;
          case "Escape":
            // Closes and hands focus back to the toggle, so the next Tab continues
            // from there rather than out of a menu that just vanished.
            if (closeMenu) closeMenu();
            toggle?.focus();
            break;
          case "ArrowDown":
          case "ArrowUp": {
            if (items.length === 0) return;

            const current = items.indexOf(document.activeElement);
            const last = items.length - 1;
            let next;
            if (current === -1) {
              // Focus sits on the menu container or the toggle, not an item yet.
              next = event.key === "ArrowDown" ? 0 : last;
            } else {
              next = event.key === "ArrowDown" ? (current + 1) % items.length : (current + last) % items.length;
            }
            items[next].focus();
            break;
          }
          default:
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        // Also block listeners bound to this same node, which stopPropagation
        // alone would leave running.
        event.stopImmediatePropagation();
      };

      globalThis.window.addEventListener("keydown", handleKeyDown, true);
      return () => globalThis.window.removeEventListener("keydown", handleKeyDown, true);
    }, [show, closeMenu, labelledBy]);

    // Don't spread Bootstrap's style - it includes transform positioning that conflicts with our portal
    return ReactDOM.createPortal(
      <div
        ref={setMenuNode}
        className={className}
        style={menuStyle}
        aria-labelledby={labelledBy}
        role="menu"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>,
      document.body,
    );
  },
);

PortalDropdownMenu.displayName = "PortalDropdownMenu";

/**
 * A dropdown that renders its menu in a portal to escape overflow:hidden containers.
 * Bootstrap's Dropdown supplies the open/close behaviour: a real toggle button, with Escape, Tab
 * and arrow keys owned by the menu's window-capture keydown listener (the menu swallows keystrokes
 * from whatever it floats over — which also blocks Bootstrap's document-level handling). The items
 * themselves are plain buttons rather than ARIA menuitems, so assistive tech announces a group of
 * buttons, not a menu.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The menu content (DropdownMenuItem components)
 * @param {boolean} props.disabled - Whether the trigger button is disabled
 * @param {string} props.menuWidth - CSS width for the menu (default: "200px")
 * @param {string} props.toggleClassName - CSS classes for the toggle button
 * @param {Object} props.toggleStyle - Inline styles for the toggle button
 * @param {string} props.toggleAriaLabel - Accessible name for the toggle. Name it for what the
 *   toggle does (open a menu), not for what the menu's items do — otherwise a screen reader is
 *   told the control destroys something it only reveals a choice about. Leave unset when
 *   `toggleLabel` already supplies visible text — setting both means the aria-label overrides the
 *   text for assistive tech, so only one should ever say the toggle's name.
 * @param {string} [props.toggleLabel] - Visible text rendered before the icon (default: none, an
 *   icon-only toggle exactly as every consumer predating this prop already renders)
 * @param {string} props.toggleIcon - FontAwesome classes for the toggle glyph
 * @param {"end"|"start"} [props.align] - Which edge of the toggle the menu lines up with (default
 *   "end", the right edge; "start" for a toggle on the left of its container)
 */
function PortalDropdown({
  children,
  disabled = false,
  menuWidth = "248px",
  toggleClassName = "btn btn-link text-secondary p-0",
  toggleStyle = {},
  toggleAriaLabel = undefined,
  toggleLabel = null,
  toggleIcon = DEFAULT_TOGGLE_ICON,
  align = "end",
}) {
  const [show, setShow] = useState(false);
  const closeMenu = useCallback(() => setShow(false), []);

  useEffect(() => {
    if (!show) return undefined;

    // Only one dropdown open at a time — see openDropdownRegistry for why the
    // outside-click close can't do this for us.
    const releaseOpenClaim = claimOpenDropdown(closeMenu);

    // The menu is positioned once, from the toggle's viewport rect, so a scroll
    // would leave it stranded beside whatever moved underneath it. Capture, because
    // scroll events from inner scrolling containers never reach window by bubbling.
    const closeOnScroll = (event) => {
      // A long menu scrolls internally; that must not close it.
      if (event.target instanceof Element && event.target.closest(".dropdown-menu")) return;

      closeMenu();
    };

    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeOnScroll, true);

    return () => {
      releaseOpenClaim();
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [show, closeMenu]);

  return (
    <PortalDropdownContext.Provider value={closeMenu}>
      <Dropdown align={align} show={show} onToggle={setShow} onMouseDown={(e) => e.stopPropagation()}>
        <Dropdown.Toggle
          as={PortalDropdownToggle}
          toggleClassName={toggleClassName}
          toggleStyle={toggleStyle}
          disabled={disabled}
          toggleAriaLabel={toggleAriaLabel}
          toggleLabel={toggleLabel}
          toggleIcon={toggleIcon}
        />

        <Dropdown.Menu as={PortalDropdownMenu} menuWidth={menuWidth} menuAlign={align}>
          {children}
        </Dropdown.Menu>
      </Dropdown>
    </PortalDropdownContext.Provider>
  );
}

PortalDropdown.propTypes = {
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  menuWidth: PropTypes.string,
  toggleClassName: PropTypes.string,
  toggleStyle: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  toggleAriaLabel: PropTypes.string,
  toggleLabel: PropTypes.string,
  toggleIcon: PropTypes.string,
  align: PropTypes.oneOf(["end", "start"]),
};

export default PortalDropdown;
